import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import QRCode from 'qrcode';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import useApiContext from 'hooks/useApiContext';
import routes from 'router/routes';
import { eRoles } from 'interfaces/Roles';
import {
  iCheckpoint,
  iCheckpointInput,
  iCheckpointList,
} from 'services/Ticketing/Ticketing.type';
import {
  card,
  formatDateTime,
  formatShortDay,
  PAGE_SX,
  primaryButton,
  sectionTitle,
  TicketingNav,
} from './common';

/** Address the hosts open on their phone (no EM account needed). */
export function checkpointUrl(token: string): string {
  return `${window.location.origin}${routes.home2.path}scan/p/${token}`;
}

function Status({ c }: { c: iCheckpoint }): JSX.Element {
  const [label, bg, fg] = c.revoked
    ? ['Désactivé', '#fee2e2', '#991b1b']
    : c.open_today
    ? ["Ouvert aujourd'hui", '#dcfce7', '#166534']
    : ["Fermé aujourd'hui", '#f3f4f6', '#374151'];
  return (
    <Box
      component="span"
      sx={{
        fontSize: 12,
        fontWeight: 600,
        px: 1,
        py: 0.25,
        borderRadius: 999,
        bgcolor: bg,
        color: fg,
      }}
    >
      {label}
    </Box>
  );
}

function Fact({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Box>
      <Typography
        sx={{ fontSize: 11, color: '#777', textTransform: 'uppercase' }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14 }}>{children}</Typography>
    </Box>
  );
}

/** Entrance checkpoints of an event: one secret scan URL per door. */
export default function Checkpoints(): JSX.Element {
  const { eventId = '' } = useParams();
  const { ticketingRepository, userRepository } = useApiContext();
  const [data, setData] = useState<iCheckpointList | null>(null);
  const [eventName, setEventName] = useState('');
  const [message, setMessage] = useState<{
    type: 'success' | 'error' | 'info';
    text: string;
  } | null>(null);
  const [editing, setEditing] = useState<iCheckpoint | 'new' | null>(null);
  const [sharing, setSharing] = useState<iCheckpoint | null>(null);

  const reload = (): void => {
    ticketingRepository
      .getCheckpoints(eventId)
      .then(setData)
      .catch((e: Error) => setMessage({ type: 'error', text: e.message }));
  };
  useEffect(reload, [eventId]);
  useEffect(() => {
    ticketingRepository
      .getScanInfo(eventId)
      .then((r) => setEventName(r.event.name))
      .catch(() => setEventName(''));
  }, [eventId]);

  const act = (p: Promise<unknown>, ok: string): void => {
    p.then(() => {
      setMessage({ type: 'success', text: ok });
      reload();
    }).catch((e: Error) => setMessage({ type: 'error', text: e.message }));
  };

  if (!data)
    return (
      <Box sx={PAGE_SX}>
        {message ? (
          <Alert severity="error">{message.text}</Alert>
        ) : (
          'Chargement…'
        )}
      </Box>
    );

  const typeName = (id: number): string =>
    data.ticket_types.find((t) => t.id === id)?.name ?? `#${id}`;

  return (
    <Box sx={PAGE_SX}>
      <TicketingNav
        current="checkpoints"
        canConfigure={userRepository.getUser()?.role !== eRoles.MODERATOR}
        eventName={eventName}
      />
      {message && (
        <Alert
          severity={message.type}
          onClose={() => setMessage(null)}
          sx={{ mb: 2 }}
        >
          {message.text}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          sx={{ flex: 1, minWidth: 280, color: '#555', fontSize: 14 }}
        >
          Chaque point de contrôle a sa propre adresse de scan, à ouvrir sur le
          téléphone des hôtes d&apos;accueil, sans compte EM. Ajoutez un code
          PIN pour qu&apos;un lien transféré par erreur ne suffise pas, et
          limitez si besoin les billets acceptés (ex. accès montage) et les
          jours d&apos;ouverture. Un téléphone perdu ? Désactivez le point ou
          générez un nouveau lien.
        </Typography>
        <Button
          variant="contained"
          onClick={() => setEditing('new')}
          sx={primaryButton}
        >
          Nouveau point de contrôle
        </Button>
      </Box>

      {data.checkpoints.length === 0 && (
        <Box sx={{ ...card, textAlign: 'center', color: '#666' }}>
          Aucun point de contrôle. Les admins et modérateurs peuvent déjà
          scanner avec leur compte EM (bouton « Ouvrir le scanner »).
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
        }}
      >
        {data.checkpoints.map((c) => (
          <Box key={c.id} sx={{ ...card, opacity: c.revoked ? 0.75 : 1 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}
            >
              <Typography sx={{ ...sectionTitle, fontSize: '16px', flex: 1 }}>
                {c.name}
              </Typography>
              <Status c={c} />
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1.5,
                mb: 2,
              }}
            >
              <Fact label="Billets acceptés">
                {c.ticket_type_ids.length
                  ? c.ticket_type_ids.map(typeName).join(', ')
                  : 'Tous'}
              </Fact>
              <Fact label="Jours">
                {c.valid_from !== null || c.valid_until !== null
                  ? `${c.valid_from ? formatShortDay(c.valid_from) : '…'} → ${
                      c.valid_until ? formatShortDay(c.valid_until) : '…'
                    }`
                  : 'Toute la durée'}
              </Fact>
              <Fact label="Code PIN">{c.has_pin ? 'Oui' : 'Non'}</Fact>
              <Fact label="Entrées">
                {c.entries_today ?? 0} aujourd&apos;hui · {c.entries ?? 0} au
                total
              </Fact>
              <Fact label="Dernier scan">
                {c.last_used_at ? formatDateTime(c.last_used_at) : '—'}
              </Fact>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {!c.revoked && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => setSharing(c)}
                  sx={{ ...primaryButton, py: 0.5 }}
                >
                  QR code et lien
                </Button>
              )}
              <Button
                size="small"
                onClick={() => setEditing(c)}
                sx={{ textTransform: 'none' }}
              >
                Modifier
              </Button>
              <Button
                size="small"
                onClick={() =>
                  act(
                    ticketingRepository.revokeCheckpoint(
                      eventId,
                      c.id,
                      !c.revoked
                    ),
                    c.revoked
                      ? 'Point de contrôle réactivé.'
                      : 'Point de contrôle désactivé : son lien ne fonctionne plus.'
                  )
                }
                sx={{ textTransform: 'none' }}
              >
                {c.revoked ? 'Réactiver' : 'Désactiver'}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  if (
                    window.confirm(
                      'Générer un nouveau lien ? L’ancien lien et les téléphones qui l’utilisent ne fonctionneront plus.'
                    )
                  )
                    act(
                      ticketingRepository.regenerateCheckpoint(eventId, c.id),
                      'Nouveau lien généré. Partagez le nouveau QR code.'
                    );
                }}
                sx={{ textTransform: 'none' }}
              >
                Nouveau lien
              </Button>
              <Button
                size="small"
                color="error"
                onClick={() => {
                  if (window.confirm(`Supprimer « ${c.name} » ?`))
                    ticketingRepository
                      .deleteCheckpoint(eventId, c.id)
                      .then((r) => {
                        setMessage({
                          type: 'success',
                          text: r.deleted
                            ? 'Point de contrôle supprimé.'
                            : 'Ce point a déjà servi : il est désactivé et conservé pour les statistiques.',
                        });
                        reload();
                      })
                      .catch((e: Error) =>
                        setMessage({ type: 'error', text: e.message })
                      );
                }}
                sx={{ textTransform: 'none', ml: 'auto' }}
              >
                Supprimer
              </Button>
            </Box>
          </Box>
        ))}
      </Box>

      {editing && (
        <CheckpointDialog
          checkpoint={editing === 'new' ? null : editing}
          types={data.ticket_types}
          onClose={() => setEditing(null)}
          onSave={async (input) => {
            const saved = await ticketingRepository.saveCheckpoint(
              eventId,
              input,
              editing === 'new' ? undefined : editing.id
            );
            setEditing(null);
            setMessage({
              type: 'success',
              text:
                editing === 'new'
                  ? 'Point de contrôle créé.'
                  : 'Point de contrôle enregistré.',
            });
            reload();
            if (editing === 'new') setSharing(saved);
          }}
        />
      )}
      {sharing && (
        <ShareDialog
          checkpoint={sharing}
          eventName={eventName}
          onClose={() => setSharing(null)}
        />
      )}
    </Box>
  );
}

function CheckpointDialog({
  checkpoint,
  types,
  onClose,
  onSave,
}: {
  checkpoint: iCheckpoint | null;
  types: iCheckpointList['ticket_types'];
  onClose: () => void;
  onSave: (input: iCheckpointInput) => Promise<void>;
}): JSX.Element {
  const [name, setName] = useState(checkpoint?.name ?? '');
  const [pin, setPin] = useState('');
  const [removePin, setRemovePin] = useState(false);
  const [typeIds, setTypeIds] = useState<number[]>(
    checkpoint?.ticket_type_ids ?? []
  );
  const [from, setFrom] = useState(checkpoint?.valid_from ?? '');
  const [until, setUntil] = useState(checkpoint?.valid_until ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const toggle = (id: number): void =>
    setTypeIds((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]
    );

  const submit = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await onSave({
        name: name.trim(),
        ...(pin ? { pin } : {}),
        ...(removePin ? { remove_pin: true } : {}),
        ticket_type_ids: typeIds,
        valid_from: from || null,
        valid_until: until || null,
      });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {checkpoint
          ? 'Modifier le point de contrôle'
          : 'Nouveau point de contrôle'}
      </DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label="Nom"
          placeholder="Entrée principale, Hall B, Accès montage…"
          value={name}
          onChange={(e) => setName(e.target.value)}
          inputProps={{ maxLength: 100 }}
          autoFocus
        />
        <Box>
          <TextField
            label={
              checkpoint?.has_pin
                ? 'Nouveau code PIN (laisser vide pour garder l’actuel)'
                : 'Code PIN (facultatif, 4 à 6 chiffres)'
            }
            value={pin}
            onChange={(e) =>
              setPin(e.target.value.replace(/\D/g, '').slice(0, 6))
            }
            inputProps={{ inputMode: 'numeric' }}
            disabled={removePin}
            fullWidth
          />
          {checkpoint?.has_pin && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={removePin}
                  onChange={(e) => setRemovePin(e.target.checked)}
                />
              }
              label="Supprimer le code PIN"
            />
          )}
          <Typography sx={{ fontSize: 12, color: '#777', mt: 0.5 }}>
            Changer le code PIN déconnecte les téléphones déjà ouverts : ils
            devront saisir le nouveau code.
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
            Billets acceptés
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#777' }}>
            Aucun coché : tous les billets sont acceptés.
          </Typography>
          {types.length === 0 && (
            <Typography sx={{ fontSize: 13, color: '#777', mt: 1 }}>
              Aucun type de billet créé pour l&apos;instant.
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {types.map((t) => (
              <FormControlLabel
                key={t.id}
                control={
                  <Checkbox
                    checked={typeIds.includes(t.id)}
                    onChange={() => toggle(t.id)}
                  />
                }
                label={t.name}
              />
            ))}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Ouvert à partir du"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Jusqu'au"
            type="date"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Annuler
        </Button>
        <Button
          variant="contained"
          disabled={!name.trim() || saving || (pin !== '' && pin.length < 4)}
          onClick={submit}
          sx={primaryButton}
        >
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ShareDialog({
  checkpoint,
  eventName,
  onClose,
}: {
  checkpoint: iCheckpoint;
  eventName: string;
  onClose: () => void;
}): JSX.Element {
  const url = checkpointUrl(checkpoint.token);
  const [qr, setQr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    QRCode.toDataURL(url, { width: 640, margin: 1 })
      .then(setQr)
      .catch(() => setQr(null));
  }, [url]);

  const copy = (): void => {
    navigator.clipboard
      ?.writeText(url)
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  };

  /** A sheet to stick at the door: QR code, name, no PIN on it. */
  const print = (): void => {
    if (!qr) return;
    if (
      !checkpoint.has_pin &&
      !window.confirm(
        'Ce point n’a pas de code PIN : quiconque flashe la feuille pourra valider des entrées. Imprimer quand même ?'
      )
    )
      return;
    const w = window.open('', '_blank', 'width=700,height=900');
    if (!w) return;
    const esc = (s: string): string =>
      s.replace(/[&<>"]/g, (ch) => `&#${ch.charCodeAt(0)};`);
    w.document
      .write(`<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8">
<title>${esc(checkpoint.name)}</title>
<style>body{font-family:Arial,sans-serif;text-align:center;padding:40px;color:#111}
img{width:420px;height:420px}h1{font-size:34px;margin:8px 0}p{color:#555}</style></head>
<body><p>${esc(eventName)}</p><h1>${esc(checkpoint.name)}</h1>
<img src="${qr}" alt=""><p>Scannez ce QR code avec le téléphone de contrôle pour ouvrir le scanner.</p>
${
  checkpoint.has_pin ? '<p>Le code PIN vous est communiqué séparément.</p>' : ''
}
<script>window.onload=function(){window.print()}</script></body></html>`);
    w.document.close();
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{checkpoint.name}</DialogTitle>
      <DialogContent sx={{ textAlign: 'center' }}>
        {qr && (
          <img
            src={qr}
            alt={`QR code du point de contrôle ${checkpoint.name}`}
            style={{ width: '100%', maxWidth: 280 }}
          />
        )}
        <Typography sx={{ fontSize: 13, color: '#555', my: 1 }}>
          Flashez ce QR code avec le téléphone de contrôle, ou envoyez-lui le
          lien.
          {checkpoint.has_pin &&
            ' Communiquez le code PIN séparément (oral, SMS).'}
        </Typography>
        <TextField
          value={url}
          fullWidth
          size="small"
          InputProps={{ readOnly: true }}
          onFocus={(e) => e.target.select()}
        />
        {!checkpoint.has_pin && (
          <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
            Sans code PIN, toute personne qui a ce lien peut valider des
            entrées.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={print} disabled={!qr} sx={{ textTransform: 'none' }}>
          Imprimer
        </Button>
        <Button onClick={copy} sx={{ textTransform: 'none' }}>
          {copied ? 'Lien copié' : 'Copier le lien'}
        </Button>
        <Button variant="contained" onClick={onClose} sx={primaryButton}>
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
